import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { createFinancialSource, updateFinancialSource, type FinancialSource } from "@/lib/api";
import { Loader2 } from "lucide-react";

export const SOURCE_TYPES = [
  { value: "bank", label: "Bank Account" },
  { value: "credit_card", label: "Credit Card" },
  { value: "investment", label: "Investment Account" },
  { value: "loan", label: "Loan / Liability" },
  { value: "payroll", label: "Payroll" },
  { value: "other", label: "Other" },
];

export const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const EMPTY_FORM = {
  source_type: "bank",
  institution_name: "",
  account_nickname: "",
  external_hint: "",
  expected_frequency: "monthly",
  notes: "",
  statement_day: "" as string | number,
  login_url: "",
};

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass to open in edit mode — form is pre-populated and calls PATCH instead of POST */
  editSource?: FinancialSource | null;
  onCreated?: (source: FinancialSource) => void;
  onUpdated?: (source: FinancialSource) => void;
}

export function AddFinancialSourceDialog({ accountId, open, onOpenChange, editSource, onCreated, onUpdated }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // Pre-populate when editing
  useEffect(() => {
    if (editSource) {
      setFormData({
        source_type: editSource.source_type || "bank",
        institution_name: editSource.institution_name || "",
        account_nickname: editSource.account_nickname || "",
        external_hint: editSource.external_hint || "",
        expected_frequency: editSource.expected_frequency || "monthly",
        notes: editSource.notes || "",
        statement_day: editSource.statement_day ?? "",
        login_url: editSource.login_url || "",
      });
    } else {
      setFormData({ ...EMPTY_FORM });
    }
  }, [editSource, open]);

  const createMutation = useMutation({
    mutationFn: () => createFinancialSource({
      account_id: accountId,
      institution_name: formData.institution_name.trim(),
      account_nickname: formData.account_nickname.trim() || undefined,
      source_type: formData.source_type,
      expected_frequency: formData.expected_frequency,
      external_hint: formData.external_hint.trim() || undefined,
      statement_day: formData.statement_day ? Number(formData.statement_day) : undefined,
      login_url: formData.login_url.trim() || undefined,
      notes: formData.notes.trim() || undefined,
    }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["financial-sources", accountId] });
      toast({ title: "Source added" });
      onCreated?.(created);
      setFormData({ ...EMPTY_FORM });
      onOpenChange(false);
    },
    onError: () => toast({ title: "Failed to add source", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateFinancialSource(editSource!.id, {
      institution_name: formData.institution_name.trim(),
      account_nickname: formData.account_nickname.trim() || null,
      source_type: formData.source_type,
      expected_frequency: formData.expected_frequency,
      external_hint: formData.external_hint.trim() || null,
      statement_day: formData.statement_day ? Number(formData.statement_day) : null,
      login_url: formData.login_url.trim() || null,
      notes: formData.notes.trim() || null,
    }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["financial-sources", accountId] });
      toast({ title: "Source updated" });
      onUpdated?.(updated);
      onOpenChange(false);
    },
    onError: () => toast({ title: "Failed to update source", variant: "destructive" }),
  });

  const isEdit = !!editSource;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const f = formData;
  const set = (k: keyof typeof EMPTY_FORM, v: string | number) =>
    setFormData((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setFormData({ ...EMPTY_FORM }); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Financial Source" : "Add Financial Source"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={f.source_type} onValueChange={(v) => set("source_type", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Frequency</Label>
              <Select value={f.expected_frequency} onValueChange={(v) => set("expected_frequency", v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((fr) => <SelectItem key={fr.value} value={fr.value}>{fr.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Institution Name <span className="text-destructive">*</span></Label>
            <Input className="h-9 text-sm" placeholder="e.g. Chase, Wells Fargo" value={f.institution_name} onChange={(e) => set("institution_name", e.target.value)} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Nickname</Label>
              <Input className="h-9 text-sm" placeholder="e.g. Operating Account" value={f.account_nickname} onChange={(e) => set("account_nickname", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hint (last 4, etc.)</Label>
              <Input className="h-9 text-sm" placeholder="e.g. x1234" value={f.external_hint} onChange={(e) => set("external_hint", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Statement Drop Day</Label>
              <Input className="h-9 text-sm" type="number" min={1} max={31} placeholder="e.g. 15" value={f.statement_day} onChange={(e) => set("statement_day", e.target.value ? parseInt(e.target.value) : "")} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Login URL</Label>
              <Input className="h-9 text-sm" placeholder="https://..." value={f.login_url} onChange={(e) => set("login_url", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea className="text-sm resize-none" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!f.institution_name.trim() || isPending}
            onClick={() => isEdit ? updateMutation.mutate() : createMutation.mutate()}
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {isEdit ? "Save Changes" : "Create Source"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
